import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ArrowRight } from "react-feather";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../design-system/components/composite/Modal/Modal";
import { ConnectorIdentity } from "../../molecules";
import { useConnectorCatalogSchemaQuery } from "../../../hooks/useConnectorApi";
import { connectorConnectService } from "../../../services/connectors/connectorConnect.service";
import {
  buildConnectorWizardConfigFromSchema,
  getConnectorWizardConfig,
} from "./connectorWizardConfig";
import { useConnectorWizard } from "./useConnectorWizard";
import { StepIndicator } from "./StepIndicator";
import { STEP_REGISTRY } from "./steps";
import {
  BiSelectCenterSlot,
  BiSelectFooterRow,
  BiSelectLeftSlot,
  BiSelectRightSlot,
  BodyStack,
  ComingSoonCard,
  ComingSoonSubtitle,
  ComingSoonTitle,
  FooterRightOnly,
  FooterRow,
  HiddenFooterAction,
  IndicatorWrap,
  OutlinePrimaryButton,
  StepBodyMeasure,
  StepBodyViewport,
} from "./ConnectorWizardModal.styles";
import type {
  ConnectorWizardModalProps,
  StepRenderProps,
} from "./ConnectorWizardModal.types";

/**
 * ConnectorWizardModal — multi-step onboarding flow for a single connector.
 *
 * Composition:
 *   - Reuses the design-system `Modal` primitive (no duplicate modal here).
 *   - `StepIndicator` reflects per-step status from `useConnectorWizard`.
 *   - The active step is looked up in `STEP_REGISTRY` by its `kind`, so
 *     adding a new step kind is purely a registry/config change.
 *
 * State + side-effects:
 *   - Form values, validation, navigation: `useConnectorWizard`.
 *   - Async work (test connection / fetch tables / finalize) lives here so the
 *     state hook stays sync. Each transition validates the current step before
 *     calling the matching API mock from `pages/Connectors/api.ts`.
 */
export const ConnectorWizardModal: React.FC<ConnectorWizardModalProps> = ({
  open,
  catalogKey,
  onClose,
  onSuccess,
}) => {
  const { data: connectorSchema, isLoading: isSchemaLoading } =
    useConnectorCatalogSchemaQuery(catalogKey, open);
  const config = useMemo(() => {
    if (connectorSchema) {
      return buildConnectorWizardConfigFromSchema(connectorSchema);
    }
    return catalogKey ? getConnectorWizardConfig(catalogKey) : null;
  }, [catalogKey, connectorSchema]);
  const resolvedCatalogKey = useMemo(
    () => config?.catalogKey ?? catalogKey ?? null,
    [config?.catalogKey, catalogKey],
  );

  const wizard = useConnectorWizard({
    config,
    resetKey: open ? "open" : "closed",
    wizardCatalogKey: catalogKey,
    persistKey: catalogKey ? `connector-wizard-draft:${catalogKey}` : null,
  });

  const {
    steps,
    currentStep,
    currentStepIndex,
    stepStatuses,
    values,
    errors,
    showErrors,
    isLoading,
    apiError,
    canAdvance,
    setValue,
    setLoading,
    setApiError,
    setCanAdvance,
    goNext,
    goBack,
    goToStep,
    validateCurrentStep,
    clearPersistedDraft,
  } = wizard;

  const measuredStepBodyRef = useRef<HTMLDivElement | null>(null);
  const previousCatalogKeyRef = useRef<string | null>(null);
  const [lockedStepBodyHeight, setLockedStepBodyHeight] = useState<
    number | undefined
  >(undefined);

  const isFirst = currentStepIndex === 0;
  const isLast = currentStepIndex === steps.length - 1;
  const selectedTableCount = Array.isArray(values.selectedTables)
    ? values.selectedTables.length
    : 0;
  const isBiVariant = config?.behavior?.uiVariant === "bi_relational_tree";
  const isBiSelectStep = Boolean(isBiVariant && currentStep?.kind === "select");

  /** Primary-action label resolved from step config. */
  const primaryLabel = useMemo(() => {
    if (!currentStep) return "Continue";
    if (isBiSelectStep) {
      if (selectedTableCount > 0) {
        return currentStep.primaryCtaLabel ?? "Import selected";
      }
      return currentStep.skipCtaLabel ?? "Import all";
    }
    if (currentStep.kind === "select" && currentStep.allowSkip) {
      const picked = Array.isArray(values.selectedTables)
        ? (values.selectedTables as unknown[]).length
        : 0;
      if (picked === 0) {
        return (
          currentStep.skipCtaLabel ?? currentStep.primaryCtaLabel ?? "Skip"
        );
      }
    }
    return currentStep.primaryCtaLabel ?? "Continue";
  }, [currentStep, isBiSelectStep, selectedTableCount, values.selectedTables]);

  const handlePrimary = useCallback(async () => {
    if (!currentStep || !resolvedCatalogKey) return;

    // OAuth short-circuit: for connectors whose credentials step used an OAuth
    // popup (e.g. Salesforce), the connection is already created server-side and
    // its id is in `values`. Skip credential-field validation and the /connect
    // re-persist (which would submit an empty form and wipe the token) — just
    // advance to the Test step, which tests the existing connection.
    const fromOAuthCredentials =
      currentStep.kind === "credentials" &&
      Boolean(currentStep.oauthProvider) &&
      typeof values.connectionId === "string" &&
      values.connectionId.trim().length > 0;
    if (fromOAuthCredentials) {
      setApiError(null);
      goNext();
      return;
    }

    // Validate current credentials step (no-op for non-credential kinds).
    if (!validateCurrentStep()) return;

    setApiError(null);

    try {
      const shouldSubmit = Boolean(currentStep.submitOnClick);

      if (shouldSubmit) {
        const fromTest =
          currentStep.kind === "test" &&
          typeof values.connectionId === "string" &&
          values.connectionId.trim().length > 0;
        if (fromTest) {
          goNext();
          return;
        }
        setLoading(true);
        const importMode =
          values.importMode === "all" || values.importMode === "selected"
            ? values.importMode
            : "selected";
        const selectedTablesForSubmit =
          importMode === "all"
            ? []
            : ((values.selectedTables as string[]) ?? []);
        const created = await connectorConnectService.create({
          catalogKey: resolvedCatalogKey,
          credentials: values,
          selectedTables: selectedTablesForSubmit,
          connectorSchema,
          shouldPatchSelectedAssets: Boolean(
            config?.behavior?.requiresAssetSelection,
          ),
        });
        setValue("connectionId", created.connectionId);
        goNext();
        return;
      }

      switch (currentStep.kind) {
        case "credentials":
        case "test":
        case "select": {
          if (isBiSelectStep) {
            setValue("importMode", selectedTableCount > 0 ? "selected" : "all");
          }
          goNext();
          return;
        }
        case "preview":
          goNext();
          return;
        case "finish":
          // Footer is hidden on Finish; this case shouldn't fire.
          return;
      }
    } catch (err) {
      setApiError(
        err instanceof Error
          ? err.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, [
    currentStep,
    resolvedCatalogKey,
    validateCurrentStep,
    setApiError,
    setLoading,
    setValue,
    values,
    goNext,
    connectorSchema,
    config?.behavior?.requiresAssetSelection,
    isBiSelectStep,
    selectedTableCount,
  ]);

  const handleBiImportAllFromSelect = useCallback(() => {
    if (!isBiSelectStep || isLoading) return;
    setValue("importMode", "all");
    goNext();
  }, [goNext, isBiSelectStep, isLoading, setValue]);

  /** Finish-step actions, surfaced via StepRenderProps so the FinishStep can
   *  render the two inline buttons from the design ("Preview and prepare" /
   *  "Add another instance"). */
  const handleFinishView = useCallback(() => {
    clearPersistedDraft();
    const connectionId =
      typeof values.connectionId === "string" ? values.connectionId : "";
    if (resolvedCatalogKey && connectionId) {
      onSuccess({ catalogKey: resolvedCatalogKey, connectionId });
    }
    onClose();
  }, [
    clearPersistedDraft,
    resolvedCatalogKey,
    values.connectionId,
    onSuccess,
    onClose,
  ]);

  const handleFinishRestart = useCallback(() => {
    clearPersistedDraft();
  }, [clearPersistedDraft]);

  // Closing the wizard abandons the in-progress draft, so the NEXT time it's
  // opened (e.g. "Add instance") it starts blank. Without this, the sessionStorage
  // draft — keyed only by connector type — leaks a previous instance's state
  // (incl. the OAuth "connected" flag + connectionId) into a fresh instance.
  const handleClose = useCallback(() => {
    clearPersistedDraft();
    onClose();
  }, [clearPersistedDraft, onClose]);

  // Step body — looked up via the registry. Falls back to the coming-soon
  // panel when the connector has no configured wizard yet.
  const stepBody = useMemo(() => {
    if (isSchemaLoading && !config) {
      return (
        <ComingSoonCard>
          <ComingSoonTitle>Loading connector setup</ComingSoonTitle>
          <ComingSoonSubtitle>
            Fetching schema-driven form configuration.
          </ComingSoonSubtitle>
        </ComingSoonCard>
      );
    }

    if (!config || !currentStep || !resolvedCatalogKey) {
      return (
        <ComingSoonCard>
          <ComingSoonTitle>Coming soon</ComingSoonTitle>
          <ComingSoonSubtitle>
            This connector doesn&apos;t have a setup flow yet. Check back soon.
          </ComingSoonSubtitle>
        </ComingSoonCard>
      );
    }

    const Renderer = STEP_REGISTRY[currentStep.kind];
    const renderProps: StepRenderProps = {
      step: currentStep,
      wizardConfig: config,
      values,
      errors,
      showErrors,
      catalogKey: resolvedCatalogKey,
      connectorSchema,
      isLoading,
      apiError,
      setValue,
      setApiError,
      setCanAdvance,
      onRequestBack: goBack,
      onFinishView: handleFinishView,
      onFinishRestart: handleFinishRestart,
    };
    return <Renderer {...renderProps} />;
  }, [
    config,
    isSchemaLoading,
    currentStep,
    resolvedCatalogKey,
    values,
    errors,
    showErrors,
    isLoading,
    apiError,
    setValue,
    setApiError,
    setCanAdvance,
    goBack,
    handleFinishView,
    handleFinishRestart,
    connectorSchema,
  ]);

  // Header identity: reuses the molecule that already powers the marketplace
  // header. The wizard subtitle rides in `providerLabel` — semantically
  // adjacent to the displayName line, no duplicate component needed.
  const header = config ? (
    <ConnectorIdentity
      displayName={config.displayName}
      iconUrl={config.iconUrl}
      providerLabel={config.subtitle}
    />
  ) : (
    <ConnectorIdentity
      displayName="Add connector"
      iconUrl=""
      providerLabel="Configure a new connection"
    />
  );

  // Reset lock before paint when modal closes or connector changes, so we do
  // not clobber the initial measurement after the first painted frame.
  useLayoutEffect(() => {
    const currentCatalogKey = resolvedCatalogKey ?? null;
    if (!open) {
      setLockedStepBodyHeight(undefined);
      previousCatalogKeyRef.current = currentCatalogKey;
      return;
    }
    if (
      previousCatalogKeyRef.current !== null &&
      previousCatalogKeyRef.current !== currentCatalogKey
    ) {
      setLockedStepBodyHeight(undefined);
    }
    previousCatalogKeyRef.current = currentCatalogKey;
  }, [open, resolvedCatalogKey]);

  // Keep a "max seen" step-body height so moving between steps never shrinks
  // the modal content area and causes flicker.
  useLayoutEffect(() => {
    if (!open) return;
    const node = measuredStepBodyRef.current;
    if (!node) return;

    const updateMaxHeight = () => {
      const nextHeight = Math.ceil(node.getBoundingClientRect().height);
      if (nextHeight <= 0) return;
      setLockedStepBodyHeight((prev) => {
        if (typeof prev !== "number") return nextHeight;
        return nextHeight > prev ? nextHeight : prev;
      });
    };

    updateMaxHeight();
    const observer = new ResizeObserver(updateMaxHeight);
    observer.observe(node);
    return () => observer.disconnect();
  }, [open, currentStepIndex, stepBody]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="xl"
      closeOnBackdropClick={!isLoading}
    >
      <ModalHeader onClose={handleClose}>{header}</ModalHeader>

      {/* padding="0" so BodyStack owns Figma's exact 16/12/12 inset. The
          design-system default modal padding is larger and was forcing
          horizontal overflow on the step indicator. */}
      <ModalBody padding="0" overflowY="hidden">
        <BodyStack>
          {config ? (
            <IndicatorWrap>
              <StepIndicator
                steps={steps}
                statuses={stepStatuses}
                currentIndex={currentStepIndex}
                onStepClick={goToStep}
              />
            </IndicatorWrap>
          ) : null}
          <StepBodyViewport $minHeightPx={lockedStepBodyHeight}>
            <StepBodyMeasure ref={measuredStepBodyRef}>{stepBody}</StepBodyMeasure>
          </StepBodyViewport>
        </BodyStack>
      </ModalBody>

      <ModalFooter>
        {currentStep?.kind !== "finish" ? (
          config ? (
            isFirst ? (
              <FooterRightOnly>
                <OutlinePrimaryButton
                  loading={isLoading}
                  disabled={!canAdvance}
                  onClick={handlePrimary}
                  iconAfter={<ArrowRight size={16} />}
                >
                  {primaryLabel}
                </OutlinePrimaryButton>
              </FooterRightOnly>
            ) : (
              <FooterRow>
                {isBiSelectStep && selectedTableCount > 0 ? (
                  <BiSelectFooterRow>
                    <BiSelectLeftSlot>
                      <OutlinePrimaryButton
                        onClick={goBack}
                        disabled={isLoading}
                      >
                        Back
                      </OutlinePrimaryButton>
                    </BiSelectLeftSlot>
                    <BiSelectCenterSlot>
                      <OutlinePrimaryButton
                        disabled={isLoading}
                        onClick={handleBiImportAllFromSelect}
                        iconAfter={<ArrowRight size={16} />}
                      >
                        {currentStep?.skipCtaLabel ?? "Import all"}
                      </OutlinePrimaryButton>
                    </BiSelectCenterSlot>
                    <BiSelectRightSlot>
                      <OutlinePrimaryButton
                        loading={isLoading}
                        disabled={!canAdvance}
                        onClick={handlePrimary}
                        iconAfter={<ArrowRight size={16} />}
                      >
                        {currentStep?.primaryCtaLabel ?? "Import selected"}
                      </OutlinePrimaryButton>
                    </BiSelectRightSlot>
                  </BiSelectFooterRow>
                ) : (
                  <>
                    <OutlinePrimaryButton onClick={goBack} disabled={isLoading}>
                      Back
                    </OutlinePrimaryButton>
                    <OutlinePrimaryButton
                      loading={isLoading}
                      disabled={isLoading || !canAdvance}
                      onClick={handlePrimary}
                      iconAfter={!isLast ? <ArrowRight size={16} /> : undefined}
                    >
                      {primaryLabel}
                    </OutlinePrimaryButton>
                  </>
                )}
              </FooterRow>
            )
          ) : (
            <FooterRightOnly>
              <OutlinePrimaryButton onClick={handleClose}>
                Close
              </OutlinePrimaryButton>
            </FooterRightOnly>
          )
        ) : (
          <FooterRightOnly aria-hidden="true">
            <HiddenFooterAction tabIndex={-1}>Placeholder</HiddenFooterAction>
          </FooterRightOnly>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default ConnectorWizardModal;
